import React, { useState, useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Typeahead } from 'react-bootstrap-typeahead';
import { toast } from 'react-toastify';

// Validation schema
const validationSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  order: Yup.number().required('Order is required').min(1, 'Order must be greater than 0'),
});

// Custom input component
const CustomInput = ({ field, form, ...props }) => {
  const hasError = form.touched[field.name] && form.errors[field.name];

  return (
    <div className={props.parentClass || 'form-group'}>
      <input
        {...field}
        {...props}
        className={`${props.className || 'form-control'} ${hasError ? 'error' : ''}`}
      />
      {hasError && (
        <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
          {form.errors[field.name]}
        </div>
      )}
    </div>
  );
};

const CreateInstrument = ({ pieceInstrumentInfo, onCreate, onEdit, onDelete, accessMode, isCreating }) => {
  const [state, setState] = useState({
    instrument_id: '',
    audio_file: '',
    audio: '',
    order: '',
    file: {},
    awsKeys: {},
    printedMusicAwsKeys: {},
    disableUpload: false,
    instrument: null,
    audioUploaded: false,
    isUploading: false,
    EditInstrument: true,
    printedUpload: false,
    printedInputOn: false,
    mediaInputOn: false,
    printedInputEditOn: false,
    mediaInputEditOn: false,
    InstrumentToEdit: null,
    title: '',
    printed: '',
    showFollowing: false,
  });

  // Refs
  const formikRef = useRef();
  const typeaheadRef = useRef();

  const onChangeSetToState = (stateKey) => (e) => {
    let updates = {
      [stateKey]: e.currentTarget.value.trim(),
    };
    if (['audio_file', 'printed'].includes(stateKey)) {
      updates.disableUpload = true;
    }
    setState(prev => ({ ...prev, ...updates }));
  };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    const { audio_file, printed, order } = state;
    const { title } = values;
    const { instrument } = state;
    const piece_id = pieceInstrumentInfo.piece.id;
    const instrument_id = instrument && instrument[0] && instrument[0].id;
    const instrumentName = instrument && instrument[0] && instrument[0].name;

    try {
      await onCreate({
        piece_id,
        instrument_id,
        instrumentName,
        audio_file,
        title,
        printed,
        order: Number(order),
      });

      pieceInstrumentInfo.getCurrentPiece('edit', piece_id);

      // Reset form and state
      resetForm();
      setState({
        instrument_id: '',
        audio_file: '',
        audio: '',
        file: {},
        awsKeys: {},
        printedMusicAwsKeys: {},
        disableUpload: false,
        instrument: null,
        audioUploaded: false,
        isUploading: false,
        title: '',
        order: '',
        printed: '',
        printedInputOn: false,
        mediaInputOn: false,
        printedInputEditOn: false,
        mediaInputEditOn: false,
        InstrumentToEdit: null,
      });

      // Clear file inputs
      try {
        document.getElementById('uploadPieceInstrument').value = null;
        document.getElementById('uploadPieceInstrumentPrinted').value = null;
      } catch (e) { }

      if (typeaheadRef.current) {
        typeaheadRef.current.getInstance().clear();
      }
    } catch (error) {
      toast.error('Error creating instrument');
    } finally {
      setSubmitting(false);
    }
  };

  const filterObjectName = (collection, id, field) => {
    return collection.filter((data) => data.id == id && data)[0][field];
  };

  const editInstrument = async () => {
    const { audio_file, title, instrument, instrument_id, id, order, printed } = state.InstrumentToEdit;
    const piece_id = pieceInstrumentInfo.piece.id;

    const instrumentName = filterObjectName(pieceInstrumentInfo.instruments, instrument_id, 'name'),
      instrumentNamePrev = filterObjectName(
        pieceInstrumentInfo.instruments,
        pieceInstrumentInfo.piece.instruments[state.showFollowing].instrument_id,
        'name'
      );

    await onEdit(
      {
        audio_file,
        title,
        instrument,
        instrument_id,
        id,
        order: Number(order),
        printed,
        instrumentName,
        instrumentNamePrev,
      },
      pieceInstrumentInfo.piece.instruments[state.showFollowing]
    );

    pieceInstrumentInfo.getCurrentPiece('edit', piece_id);
    setState(prevState => ({
      ...prevState,
      instrument_id: '',
      audio_file: '',
      audio: '',
      file: {},
      awsKeys: {},
      printedMusicAwsKeys: {},
      disableUpload: false,
      instrument: null,
      audioUploaded: false,
      isUploading: false,
      title: '',
      order: '',
      showFollowing: false,
      printed: '',
      printedInputOn: false,
      mediaInputOn: false,
      printedInputEditOn: false,
      mediaInputEditOn: false,
      InstrumentToEdit: null,
    }));

    if (typeaheadRef.current) {
      typeaheadRef.current.getInstance().clear();
    }
  };

  const onFileChange = async (eve) => {
    setState(prevState => ({
      ...prevState,
      audioUploaded: false,
      disableUpload: false,
    }));
    const file = eve.target.files[0];
    const getAwsKey = pieceInstrumentInfo.getAwsKey;
    const fileName = file.name.replace(/ /g, '_');
    const awsKeys = await getAwsKey({ key: `pieces/instruments/audios/${fileName}` });
    setState(prevState => ({
      ...prevState,
      file,
      awsKeys,
      disableUpload: true,
    }));
    if (state.InstrumentToEdit) {
      var AudioFile = state.InstrumentToEdit;
      AudioFile['audio_file'] = `${awsKeys.action}${awsKeys.key}`;
      setState(prev => ({
        ...prev,
        InstrumentToEdit: AudioFile,
      }));
    } else {
      setState(prev => ({ ...prev, audio_file: `${awsKeys.action}${awsKeys.key}` }));
    }
  };

  const onFileChangePrinted = async (eve) => {
    setState(prevState => ({
      ...prevState,
      printedUploaded: false,
      disableUpload: false,
    }));
    const file = eve.target.files[0];
    const getAwsKey = pieceInstrumentInfo.getAwsKey;
    const fileName = file.name.replace(/ /g, '_');
    const printedMusicAwsKeys = await getAwsKey({ key: `pieces/instruments/printed/${fileName}` });
    setState(prevState => ({
      ...prevState,
      file,
      printedMusicAwsKeys,
      disableUpload: true,
    }));
    if (state.InstrumentToEdit) {
      var PrintedFile = state.InstrumentToEdit;
      PrintedFile['printed'] = `${printedMusicAwsKeys.action}${printedMusicAwsKeys.key}`;
      setState(prev => ({
        ...prev,
        InstrumentToEdit: PrintedFile,
      }));
    } else {
      setState(prev => ({ ...prev, printed: `${printedMusicAwsKeys.action}${printedMusicAwsKeys.key}` }));
    }
  };

  const handleEditStateChange = (key, value) => {
    let { InstrumentToEdit } = state;
    var instrumentX = InstrumentToEdit;
    instrumentX[key] = value;
    setState(prev => ({
      ...prev,
      InstrumentToEdit: instrumentX,
    }));
  };

  const uploadAudio = (label) => async (eve) => {
    eve.preventDefault();
    if (
      label !== 'editInstrument' &&
      ((state.printedInputOn && !state.printedMusicAwsKeys.action) ||
        (state.mediaInputOn && !state.awsKeys.action))
    ) {
      toast.error('please try again.');
    }
    if (label === 'editInstrument') {
      if (Object.keys(state.awsKeys).length) {
        var form = document.getElementById('uploadAudioEdit');
        var data = new FormData(form);
      }

      if (Object.keys(state.printedMusicAwsKeys).length) {
        var form2 = document.getElementById('uploadFileEdit');
        var data2 = new FormData(form2);
      }
    } else {
      if (Object.keys(state.printedMusicAwsKeys).length) {
        var form2 = document.getElementById('uploadFile');
        var data2 = new FormData(form2);
      }

      if (Object.keys(state.awsKeys).length) {
        var form = document.getElementById('uploadAudio');
        var data = new FormData(form);
      }
    }
    try {
      console.log('check aud');
      let checkAud = false, checkPrintedAud = false;
      setState(prevState => ({
        ...prevState,
        isUploading: true,
      }));
      if (Object.keys(state.awsKeys).length && state.awsKeys.action) {
        await fetch(state.awsKeys.action, { method: 'POST', body: data });
        checkAud = true;
      }
      if (Object.keys(state.printedMusicAwsKeys).length && state.printedMusicAwsKeys.action) {
        await fetch(state.printedMusicAwsKeys.action, { method: 'POST', body: data2 });
        checkPrintedAud = true;
      }

      if (checkAud) {
        toast.success('Audio uploaded successfully.');
      }

      if (checkPrintedAud) {
        toast.success('Printed audio uploaded successfully.');
      }

      setState(prevState => ({
        ...prevState,
        audioUploaded: true,
        fileUpload: true,
      }));
      if (label === 'editInstrument') {
        editInstrument();
      } else {
        // Trigger form submission
        if (formikRef.current) {
          formikRef.current.handleSubmit();
        }
      }
    } catch (error) {
      toast.error('Something went wrong while uploading video.');
    }
  };

  const handleDelete = async (instrument) => {
    await onDelete(instrument);
    const piece_id = pieceInstrumentInfo.piece.id;
    pieceInstrumentInfo.getCurrentPiece('edit', piece_id);
  };

  const Show = (i) => {
    const InstrumentToEditArray = pieceInstrumentInfo.piece.instruments.filter((instrument, id) => {
      if (id == i) {
        return instrument;
      }
    });
    const InstrumentToEditObject = InstrumentToEditArray[0];
    setState(prev => ({
      ...prev,
      showFollowing: i,
      InstrumentToEdit: JSON.parse(JSON.stringify(InstrumentToEditObject)),
    }));
  };

  const getPieceInstrument = () => {
    const { awsKeys, disableUpload, printedMusicAwsKeys, mediaInputEditOn, printedInputEditOn } = state;
    return (
      pieceInstrumentInfo.piece &&
      pieceInstrumentInfo.piece.instruments &&
      pieceInstrumentInfo.piece.instruments.map((instrument, i) => {
        return (
          <div className="instrument-list" key={instrument.id}>
            {accessMode === 'edit' && (
              <div className="row">
                <span
                  style={{ display: 'inline-block', float: 'right', color: 'red' }}
                  className="glyphicon glyphicon-remove-circle"
                  aria-hidden="true"
                  onClick={() => handleDelete(instrument)}
                />
                <span
                  style={{ display: 'inline-block', float: 'right', marginRight: '10px', color: 'green' }}
                  className="glyphicon glyphicon-edit"
                  aria-hidden="true"
                  onClick={() => Show(i)}
                />
              </div>
            )}
            {state.showFollowing !== i && (
              <div>
                <div className="row">
                  <div className="col-md-3">
                    <label>Title</label>
                  </div>
                  <div className="col-md-9">{instrument.title}</div>
                </div>
                <div className="row">
                  <div className="col-md-3">
                    <label>Order</label>
                  </div>
                  <div className="col-md-9">{instrument.order}</div>
                </div>
                <div className="row">
                  <div className="col-md-3">
                    <label>Audio File</label>
                  </div>
                  <div className="col-md-9">{instrument.audio_file}</div>
                </div>
                <div className="row">
                  <div className="col-md-3">
                    <label>Printed Music</label>
                  </div>
                  <div className="col-md-9">{instrument.printed}</div>
                </div>
              </div>
            )}
            {state.showFollowing === i && (
              <div>
                <div className="row">
                  <div className="col-md-3">
                    <label>Instrument</label>
                  </div>
                  <div className="col-md-9">
                    <Typeahead
                      onChange={(selected) => {
                        if (selected.length !== 0) {
                          var instrumentProperty = state.InstrumentToEdit;
                          instrumentProperty.instrument = selected[0];
                          instrumentProperty.instrument_id = selected[0].id;
                          setState(prev => ({ ...prev, InstrumentToEdit: instrumentProperty }));
                        }
                      }}
                      labelKey="name"
                      options={pieceInstrumentInfo.instruments}
                      selected={[state.InstrumentToEdit.instrument]}
                      className="form-group"
                      placeholder="Instruments"
                    />
                  </div>
                </div>
                <div className="editButtons">
                  <button
                    type="submit"
                    name="submit"
                    className="cus-btn-primary btn-sm submitButton"
                    style={{ margin: '10px 10px 10px 10px' }}
                    onClick={uploadAudio('editInstrument')}
                  >
                    {isCreating || state.isUploading ? <span className="spinner" /> : <span>Save</span>}
                  </button>
                  <button
                    type="button"
                    name="button"
                    className="cus-btn-danger btn-sm submitButton"
                    style={{ margin: '10px 10px 10px 10px' }}
                    onClick={() => {
                      setState(prev => ({
                        ...prev,
                        showFollowing: false,
                        InstrumentToEdit: null,
                      }));
                    }}
                  >
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            )}
            <hr />
          </div>
        );
      })
    );
  };

  const handleStateChange = (key, val) => (e) => {
    e.stopPropagation();
    let updates = { [key]: val };
    if (['printedInputEditOn', 'printedInputOn'].includes(key)) {
      updates['printed'] = '';
      updates['printedMusicAwsKeys'] = {};
    } else if (['mediaInputEditOn', 'mediaInputOn'].includes(key)) {
      updates['audio_file'] = '';
      updates['awsKeys'] = {};
    }

    setState(prev => ({ ...prev, ...updates }));
  };

  const { awsKeys, disableUpload, printedMusicAwsKeys, mediaInputOn, printedInputOn } = state;

  return (
    <section className="well create-instrument">
      {getPieceInstrument()}

      {accessMode === 'edit' && (
        <Formik
          ref={formikRef}
          initialValues={{
            title: '',
            order: '',
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, isValid, dirty, values }) => (
            <Form>
              <div className="row">
                <div className="col-md-3">
                  <label>Instrument</label>
                </div>
                <div className="col-md-9">
                  <Typeahead
                    onChange={(selected) => {
                      setState(prev => ({
                        ...prev,
                        instrument: selected,
                      }));
                    }}
                    ref={typeaheadRef}
                    labelKey="name"
                    options={pieceInstrumentInfo.instruments}
                    selected={state.instrument}
                    className="form-group"
                    placeholder="Instruments"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <label>Title</label>
                </div>
                <div className="col-md-9">
                  <Field
                    name="title"
                    placeholder="Title"
                    component={CustomInput}
                    type="text"
                    className="form-control"
                    parentClass="form-group"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <label>Order</label>
                </div>
                <div className="col-md-9">
                  <Field
                    name="order"
                    placeholder="Order"
                    component={CustomInput}
                    type="number"
                    className="form-control"
                    parentClass="form-group"
                  />
                </div>
              </div>

              {/* Audio upload section */}
              <div className="row">
                <div className="col-md-3">
                  <label>Audio</label>
                </div>
                <div className="col-md-9 radio-part">
                  <div className="radio-selection row">
                    <div className="col-md-4">
                      <input
                        type="radio"
                        id="linkTypeMedia"
                        name="mediaType"
                        checked={!mediaInputOn}
                        onChange={handleStateChange('mediaInputOn', false)}
                      />
                      &nbsp;
                      <label htmlFor="linkTypeMedia">Link</label>
                    </div>
                    <div className="col-md-4">
                      <input
                        type="radio"
                        id="uploadTypeMedia"
                        name="mediaType"
                        checked={mediaInputOn}
                        onChange={handleStateChange('mediaInputOn', true)}
                      />
                      &nbsp;
                      <label htmlFor="uploadTypeMedia">Upload</label>
                    </div>
                  </div>

                  {mediaInputOn ? (
                    <form id="uploadAudio" method="post" encType="multipart/form-data">
                      <input type="hidden" name="key" value={awsKeys.key || 'NA'} />
                      <input type="hidden" name="acl" value={awsKeys.acl || 'NA'} />
                      <input type="hidden" name="X-Amz-Credential" value={awsKeys['x-amz-credential'] || 'NA'} />
                      <input type="hidden" name="X-Amz-Algorithm" value={awsKeys['x-amz-algorithm'] || 'NA'} />
                      <input type="hidden" name="X-Amz-Date" value={awsKeys['x-amz-date'] || 'NA'} />
                      <input type="hidden" name="Policy" value={awsKeys.policy || 'NA'} />
                      <input type="hidden" name="X-Amz-Signature" value={awsKeys['x-amz-signature'] || 'NA'} />
                      <input id="uploadPieceInstrument" type="file" name="file" onChange={onFileChange} />
                    </form>
                  ) : (
                    <input
                      name="audio_file"
                      placeholder="Audio"
                      value={state.audio_file}
                      onChange={onChangeSetToState('audio_file')}
                      className={`form-control ${accessMode === 'view' ? 'disabled' : ''}`}
                    />
                  )}
                </div>
              </div>

              {/* Printed section */}
              <div className="row">
                <div className="col-md-3">
                  <label>Printed</label>
                </div>
                <div className="col-md-9 radio-part">
                  <div className="radio-selection row">
                    <div className="col-md-4">
                      <input
                        type="radio"
                        id="linkTypePrinted"
                        name="printedType"
                        checked={!printedInputOn}
                        onChange={handleStateChange('printedInputOn', false)}
                      />
                      &nbsp;
                      <label htmlFor="linkTypePrinted">Link</label>
                    </div>
                    <div className="col-md-4">
                      <input
                        type="radio"
                        id="uploadTypePrinted"
                        name="printedType"
                        checked={printedInputOn}
                        onChange={handleStateChange('printedInputOn', true)}
                      />
                      &nbsp;
                      <label htmlFor="uploadTypePrinted">Upload</label>
                    </div>
                  </div>

                  {printedInputOn ? (
                    <form id="uploadFile" method="post" encType="multipart/form-data">
                      <input type="hidden" name="key" value={printedMusicAwsKeys.key || 'NA'} />
                      <input type="hidden" name="acl" value={printedMusicAwsKeys.acl || 'NA'} />
                      <input
                        type="hidden"
                        name="X-Amz-Credential"
                        value={printedMusicAwsKeys['x-amz-credential'] || 'NA'}
                      />
                      <input
                        type="hidden"
                        name="X-Amz-Algorithm"
                        value={printedMusicAwsKeys['x-amz-algorithm'] || 'NA'}
                      />
                      <input type="hidden" name="X-Amz-Date" value={printedMusicAwsKeys['x-amz-date'] || 'NA'} />
                      <input type="hidden" name="Policy" value={printedMusicAwsKeys.policy || 'NA'} />
                      <input
                        type="hidden"
                        name="X-Amz-Signature"
                        value={printedMusicAwsKeys['x-amz-signature'] || 'NA'}
                      />
                      <input
                        id="uploadPieceInstrumentPrinted"
                        type="file"
                        name="file"
                        onChange={onFileChangePrinted}
                      />
                    </form>
                  ) : (
                    <input
                      name="printed"
                      placeholder="Printed"
                      value={state.printed}
                      onChange={onChangeSetToState('printed')}
                      className={`form-control ${accessMode === 'view' ? 'disabled' : ''}`}
                    />
                  )}
                </div>
              </div>

              <button
                onClick={uploadAudio('newInstrument')}
                type="button"
                name="submit"
                className="cus-btn-primary btn-block btn-lg submitButton"
                disabled={
                  isCreating ||
                  isSubmitting ||
                  !(disableUpload && pieceInstrumentInfo.piece && state.instrument && values.title)
                }
              >
                {isCreating || state.isUploading || isSubmitting ? (
                  <span className="spinner" />
                ) : (
                  <span>Add Instrument</span>
                )}
              </button>
            </Form>
          )}
        </Formik>
      )}
    </section>
  );
};

export default CreateInstrument;
